/**
 * AddonItems
 * Component to handle the addon items as a group.
 */
if (!customElements.get('addon-items')) {
  customElements.define(
    'addon-items',
    class AddonItems extends HTMLElement {
      constructor() {
        super();
        this.handleChangeBind = this.handleChange.bind(this);
        this.observer;
      }

      connectedCallback() {
        this.splitTestClass = this.dataset.splitTestClass;
        this.productFormId = this.dataset.productFormId;

        // Check if we're split testing
        this.htmlHasSplitTestClass = document.documentElement.classList.contains(this.splitTestClass);
        if (this.splitTestClass && !this.htmlHasSplitTestClass) {
          this.initObserver()
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleChangeBind);
        this.observer?.disconnect();
      }

      init() {
        this.productForm = document.querySelector(`#${ this.productFormId }`);
        this.appendUpsellScript(this.buildItemArray());
        this.addEventListener('change', this.handleChangeBind);
      }

      /**
       * Observer to watch for a class name on the HTML element
       * Class name will be added based on the split test variation in Convert
       */
      initObserver() {
        this.observer = new MutationObserver((mutations) => { 
          mutations.forEach((mutation) => {
            if (mutation.target.classList.contains(this.splitTestClass)) {
              this.init();
              this.observer?.disconnect();
            }
          });
        });
      
        this.observer.observe(document.documentElement, { 
          attributes: true, 
          attributeOldValue: true, 
          attributeFilter: ['class'] 
        });
      }

      /**
       * Change event for addon selection
       * @param {Event} evt 
       * @returns 
       */
      handleChange(evt) {
        const selectedAddonInput = evt.target.closest('[data-addon-item]');
        if (!selectedAddonInput) return;
        this.appendUpsellScript(this.buildItemArray(selectedAddonInput));

        // Publish addon change event for price updating in the PDPPriceModifier class
        publish(PUB_SUB_EVENTS.addonChange);
      }

      /**
       * Can be called on change event or on init
       * Find the selected checkbox
       * @param {*} selectedAddonInput 
       * @returns 
       */
      buildItemArray(selectedAddonInput) {
        selectedAddonInput = selectedAddonInput || this.querySelector('[data-addon-item]:checked');

        const items = [];

        if (selectedAddonInput) {
          items.push({
            id: selectedAddonInput.dataset.variantId,
            quantity: selectedAddonInput.dataset.quantity || 1,
            price: selectedAddonInput.dataset.price || 0,
            compareAtPrice: selectedAddonInput.dataset.compareAtPrice || 0
          });
        }

        return items;
      }

      /**
       * Creates a script tag containing an array of the selected addon products 
       * and appends it to the PDP form.
       * This script is used in product-form.js
       * @param {*} addonItems 
       * @returns 
       */
      appendUpsellScript(addonItems) {
        this.removeUpsellScript();
        if (addonItems.length < 1) return;
        // Create a script tag to hold the addon item array
        const addonScript = document.createElement('script');
        addonScript.type = 'text/javascript';
        addonScript.setAttribute('data-addon-item-array', '');
        addonScript.textContent = JSON.stringify(addonItems);
        this.productForm.appendChild(addonScript);
      }

      /**
       * Deletes the addon item array
       */
      removeUpsellScript() {
        const addonScript = this.productForm.querySelector('[data-addon-item-array]');
        if (addonScript) {
          addonScript.remove();
        }
      }
    }
  );
}

/**
 * AddonItem
 * Component for each individual addon item.
 * Handles the drop down open/close
 */
if (!customElements.get('addon-item')) {
  customElements.define(
    'addon-item',
    class AddonItem extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.accordion = this.querySelector('.addon-item__accordion');

        // If the addon has accordion content
        // Set the height custom prop
        // Create a resize observer to always know the correct content height
        if (this.accordion) {
          this.updateAccordionHeight();

          this.widthObserver = new ResizeObserver(() => {
            this.updateAccordionHeight();
          });
          this.widthObserver.observe(this);
        }
      }

      disconnectedCallback() {
        this.widthObserver?.disconnect();
      }

      /**
       * Calculates the height of the accordion content and sets a CSS custom property to use the height in a CSS transition when the addon is selected.
       */
      updateAccordionHeight() {
        const elementHeight = this.accordion.offsetHeight;
        this.style.setProperty("--addon-item-accordion-height", `${elementHeight}px`);
      }
    }
  );
}
